/**
 * Donation Related Pages
 * Pages for handling donations via Stripe
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Cookie, Heart, CheckCircle, X, Gift, ExternalLink, Package, CreditCard, Loader2 } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

import { API } from "../lib/api";

const DonatePage = () => {
  const [amount, setAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [donorEmail, setDonorEmail] = useState("");
  const [message, setMessage] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [processingStripe, setProcessingStripe] = useState(false);
  const navigate = useNavigate();

  const presetAmounts = [5, 10, 20, 50];
  const donationAmount = parseFloat(amount) || 0;

  const handleProceedToPayment = () => {
    if (!donationAmount || donationAmount < 1) {
      toast.error("Please enter a valid amount (minimum £1)");
      return;
    }
    setShowPayment(true);
  };

  // Stripe checkout
  const handleStripeCheckout = async () => {
    if (!donationAmount || donationAmount < 1) return;
    
    setProcessingStripe(true);
    try {
      const res = await axios.post(`${API}/stripe/checkout/donation`, {
        amount: donationAmount,
        origin_url: window.location.origin,
        donor_name: donorName || null,
        donor_email: donorEmail || null,
        message: message || null,
      }, { withCredentials: true });
      
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error("Stripe checkout error:", err);
      toast.error(err.response?.data?.detail || "Failed to start checkout");
      setProcessingStripe(false);
    }
  };

  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-amber-50 via-stone-50 to-amber-50" data-testid="donate-page">
      <div className="max-w-4xl mx-auto px-6 py-12">
        
        {/* Amazon Wish List Banner */}
        <a 
          href="https://amzn.eu/0hIylSLq" 
          target="_blank" 
          rel="noopener noreferrer"
          className="block mb-12 group"
          data-testid="amazon-wishlist-link"
        >
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#FF9900] via-[#FFB84D] to-[#FF9900] p-1 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]">
            <div className="relative bg-gradient-to-r from-[#232F3E] to-[#37475A] rounded-xl p-6 md:p-8">
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#FF9900]/10 rounded-full -translate-y-1/2 translate-x-1/2" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-[#FF9900]/10 rounded-full translate-y-1/2 -translate-x-1/2" />
              
              <div className="relative flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-[#FF9900] rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <Gift className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center md:text-left">
                    <h2 className="text-2xl md:text-3xl font-bold text-white mb-1">Amazon Wish List</h2>
                    <p className="text-[#FF9900] text-sm md:text-base">Send supplies directly to our rescue cats! 🐱</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 bg-[#FF9900] hover:bg-[#FFB84D] text-[#232F3E] font-bold px-6 py-3 rounded-full transition-colors shadow-lg">
                  <Package className="w-5 h-5" />
                  <span>View Wish List</span>
                  <ExternalLink className="w-4 h-4" />
                </div>
              </div>
              
              <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2">
                {["Food", "Treats", "Toys", "Bedding", "Litter", "Medical Supplies"].map(item => (
                  <span key={item} className="bg-white/10 text-white/90 text-xs px-3 py-1 rounded-full">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </a>

        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Cookie className="w-10 h-10 text-amber-600" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">Buy the Cats Some Treats!</h1>
          <p className="text-stone-600 max-w-2xl mx-auto text-lg">
            Your donation helps us provide food, treats, toys, and veterinary care for our rescue cats. 
            Every penny makes a difference!
          </p>
        </div>

        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8">
            <div className="space-y-6">
              {/* Preset amounts */}
              <div>
                <Label className="mb-3 block">Quick Amount</Label>
                <div className="grid grid-cols-4 gap-2">
                  {presetAmounts.map(preset => (
                    <Button
                      key={preset}
                      variant={amount === preset.toString() ? "default" : "outline"}
                      onClick={() => { setAmount(preset.toString()); setShowPayment(false); }}
                      className={amount === preset.toString() ? "bg-amber-600 hover:bg-amber-700" : ""}
                    >
                      £{preset}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Custom amount */}
              <div>
                <Label htmlFor="amount">Or Enter Custom Amount (£)</Label>
                <Input
                  id="amount"
                  type="number"
                  min="1"
                  step="0.01"
                  value={amount}
                  onChange={(e) => { setAmount(e.target.value); setShowPayment(false); }}
                  placeholder="Enter amount"
                  className="text-lg"
                />
              </div>

              {/* Optional details */}
              <div className="space-y-4 pt-4 border-t">
                <p className="text-sm text-stone-500">Optional (for thank you message)</p>
                <div>
                  <Label htmlFor="donorName">Your Name</Label>
                  <Input
                    id="donorName"
                    value={donorName}
                    onChange={(e) => setDonorName(e.target.value)}
                    placeholder="Anonymous"
                  />
                </div>
                <div>
                  <Label htmlFor="donorEmail">Email</Label>
                  <Input
                    id="donorEmail"
                    type="email"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    placeholder="For receipt"
                  />
                </div>
                <div>
                  <Label htmlFor="message">Leave a Message</Label>
                  <Textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Your kind words..."
                    rows={3}
                  />
                </div>
              </div>

              {/* Payment Section */}
              {!showPayment ? (
                <Button
                  onClick={handleProceedToPayment}
                  disabled={!amount || donationAmount < 1}
                  className="w-full bg-amber-600 hover:bg-amber-700 text-lg py-6 rounded-full"
                >
                  <Heart className="w-5 h-5 mr-2 fill-white" />
                  Donate {donationAmount >= 1 ? `£${donationAmount.toFixed(2)}` : ""}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="font-semibold text-amber-800">
                      Donating £{donationAmount.toFixed(2)}
                    </p>
                    <button 
                      onClick={() => setShowPayment(false)}
                      className="text-sm text-amber-600 hover:underline mt-1"
                    >
                      Change amount
                    </button>
                  </div>
                  
                  <Button
                    onClick={handleStripeCheckout}
                    disabled={processingStripe}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-lg py-6 rounded-lg"
                  >
                    {processingStripe ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Redirecting to Checkout...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-5 h-5 mr-2" />
                        Pay £{donationAmount.toFixed(2)} with Card
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-stone-500">
                    Secure checkout powered by Stripe. All major cards accepted.
                  </p>
                </div>
              )}

              <p className="text-xs text-center text-stone-400">
                Your donation is securely processed. Thank you for supporting our cats!
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Impact section */}
        <div className="mt-12 text-center">
          <h2 className="text-2xl font-bold text-stone-900 mb-6">Where Your Donation Goes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { amount: "£5", impact: "A day's worth of tasty treats for the cats" },
              { amount: "£10", impact: "A cozy new toy or bed for a rescue cat" },
              { amount: "£20", impact: "Veterinary check-up or vaccinations" },
            ].map(item => (
              <Card key={item.amount}>
                <CardContent className="p-6 text-center">
                  <Badge className="bg-amber-100 text-amber-700 mb-3 text-lg">{item.amount}</Badge>
                  <p className="text-stone-600">{item.impact}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Success page
const DonationSuccessPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen pt-36 bg-gradient-to-br from-green-50 via-stone-50 to-amber-50 flex items-center justify-center" data-testid="donation-success-page">
      <Card className="max-w-md mx-4 text-center">
        <CardContent className="p-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-4">Thank You! 🐱</h1>
          <p className="text-stone-600 mb-6">
            Your generous donation will help us care for our rescue cats. 
            The cats send their purrs and headbutts!
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate("/")} className="w-full bg-amber-600 hover:bg-amber-700 rounded-full">
              Back to Home
            </Button>
            <Button onClick={() => navigate("/cats")} variant="outline" className="w-full rounded-full">
              Meet Our Cats
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Cancel page
const DonationCancelPage = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen pt-36 bg-stone-50 flex items-center justify-center" data-testid="donation-cancel-page">
      <Card className="max-w-md mx-4 text-center">
        <CardContent className="p-8">
          <div className="w-20 h-20 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <X className="w-10 h-10 text-stone-400" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-4">Donation Cancelled</h1>
          <p className="text-stone-600 mb-6">
            No worries! If you'd like to support our cats another way, 
            consider checking out our Amazon Wish List.
          </p>
          <div className="space-y-3">
            <Button onClick={() => navigate("/donate")} className="w-full bg-amber-600 hover:bg-amber-700 rounded-full">
              Try Again
            </Button>
            <Button onClick={() => navigate("/")} variant="outline" className="w-full rounded-full">
              Back to Home
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { DonatePage, DonationSuccessPage, DonationCancelPage };
